import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { sendFollowNotification } from '@/services/triggerNotification';

export const useFollow = (targetUserId: string | undefined) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if current user follows target user
  const { data: isFollowing, isLoading } = useQuery({
    queryKey: ['follow', user?.id, targetUserId],
    queryFn: async () => {
      if (!user?.id || !targetUserId || user.id === targetUserId) return false;
      
      const { data, error } = await supabase
        .from('follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', targetUserId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    },
    enabled: !!user?.id && !!targetUserId && user.id !== targetUserId,
  });

  // Get follower count for target user
  const { data: followerCount = 0 } = useQuery({
    queryKey: ['followerCount', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return 0;
      
      const { count, error } = await supabase
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', targetUserId);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!targetUserId,
  });

  // Get following count for target user
  const { data: followingCount = 0 } = useQuery({
    queryKey: ['followingCount', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return 0;
      
      const { count, error } = await supabase
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', targetUserId);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!targetUserId,
  });

  // Follow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !targetUserId) throw new Error('Missing user data');
      
      const { error } = await supabase
        .from('follows')
        .insert({ follower_id: user.id, following_id: targetUserId });
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['follow', user?.id, targetUserId] });
      queryClient.invalidateQueries({ queryKey: ['followerCount', targetUserId] });
      queryClient.invalidateQueries({ queryKey: ['followingCount', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success('Successfully followed user');
      
      // Send notification to the followed user
      if (targetUserId && user?.id) {
        sendFollowNotification(targetUserId, user.id).catch(console.error);
      }
    },
    onError: (error: any) => {
      toast.error('Failed to follow user: ' + error.message);
    },
  });

  // Unfollow mutation
  const unfollowMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !targetUserId) throw new Error('Missing user data');
      
      const { error } = await supabase
        .from('follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', targetUserId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['follow', user?.id, targetUserId] });
      queryClient.invalidateQueries({ queryKey: ['followerCount', targetUserId] });
      queryClient.invalidateQueries({ queryKey: ['followingCount', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success('Successfully unfollowed user');
    },
    onError: (error: any) => {
      toast.error('Failed to unfollow user: ' + error.message);
    },
  });

  const handleToggleFollow = () => {
    if (!user?.id || !targetUserId || user.id === targetUserId) return;
    
    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  return {
    isFollowing: isFollowing || false,
    followerCount,
    followingCount,
    isLoading,
    isToggling: followMutation.isPending || unfollowMutation.isPending,
    toggleFollow: handleToggleFollow,
  };
};
